import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UploadedFile, StorybookProject } from '@/types';
import type { Language } from '@/i18n';
import type { StyleQuizAnswers, StyleRecommendation } from '@/utils/styleRecommendations';

interface AbandonedStep {
  stepIndex: number;
  stepId: string;
  timestamp: number;
  sessionDuration: number;
}

interface StoreState {
  // Language
  language: Language;
  setLanguage: (lang: Language) => void;
  
  // Wizard state
  currentStep: number;
  uploadedFile: UploadedFile | null;

  
  // Session tracking
  wizardStartTime: number | null;
  abandonedSteps: AbandonedStep[];
  
  // Completed steps tracking
  completedSteps: string[];
  setCompletedSteps: (steps: string[] | ((prev: string[]) => string[])) => void;
  
  // Wizard actions
  setCurrentStep: (step: number) => void;
  setUploadedFile: (file: UploadedFile | null) => void;

  resetWizard: () => void;

  // Session tracking actions
  setWizardStartTime: (time: number | null) => void;
  trackAbandonedStep: (stepIndex: number, stepId: string) => void;
  clearAbandonedSteps: () => void;

  // Style quiz
  styleQuizAnswers: StyleQuizAnswers | null;
  styleRecommendation: StyleRecommendation | null;
  setStyleQuizAnswers: (answers: StyleQuizAnswers | null) => void;
  setStyleRecommendation: (rec: StyleRecommendation | null) => void;

  // Storybook state
  storybookStep: number;
  setStorybookStep: (step: number) => void;
  currentStorybookProject: StorybookProject | null;
  setCurrentStorybookProject: (project: StorybookProject | null) => void;
  resetStorybook: () => void;

  // Animation state
  animationStep: number;
  setAnimationStep: (step: number) => void;
  currentAnimationProject: StorybookProject | null;
  setCurrentAnimationProject: (project: StorybookProject | null) => void;
  resetAnimation: () => void;
}

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      // Language
      language: 'fa' as Language,
      setLanguage: (lang) => set({ language: lang }),
      
      // Wizard state
      currentStep: 0,
      uploadedFile: null,

      
      // Session tracking
      wizardStartTime: null,
      abandonedSteps: [],
      
      // Completed steps tracking
      completedSteps: [],
      setCompletedSteps: (steps) => set((state) => ({
        completedSteps: typeof steps === 'function' ? steps(state.completedSteps) : steps
      })),
      
      // Wizard actions
      setCurrentStep: (step) => set({ currentStep: step }),
      setUploadedFile: (file) => set({ uploadedFile: file }),
      
      resetWizard: () => set({
        currentStep: 0,
        uploadedFile: null,

        completedSteps: [],
        wizardStartTime: null,
        styleQuizAnswers: null,
        styleRecommendation: null,
        // Do NOT clear abandonedSteps — preserve analytics data
      }),

      // Session tracking actions
      setWizardStartTime: (time) => set({ wizardStartTime: time }),
      trackAbandonedStep: (stepIndex, stepId) => set((state) => ({
        abandonedSteps: [
          ...state.abandonedSteps,
          {
            stepIndex,
            stepId,
            timestamp: Date.now(),
            sessionDuration: state.wizardStartTime
              ? Date.now() - state.wizardStartTime
              : 0,
          }
        ]
      })),
      clearAbandonedSteps: () => set({ abandonedSteps: [] }),

      // Style quiz
      styleQuizAnswers: null,
      styleRecommendation: null,
      setStyleQuizAnswers: (answers) => set({ styleQuizAnswers: answers }),
      setStyleRecommendation: (rec) => set({ styleRecommendation: rec }),

      // Storybook state
      storybookStep: 0,
      setStorybookStep: (step) => set({ storybookStep: step }),
      currentStorybookProject: null,
      setCurrentStorybookProject: (project) => set({ currentStorybookProject: project }),
      resetStorybook: () => set({
        storybookStep: 0,
        currentStorybookProject: null,
      }),

      // Animation state
      animationStep: 0,
      setAnimationStep: (step) => set({ animationStep: step }),
      currentAnimationProject: null,
      setCurrentAnimationProject: (project) => set({ currentAnimationProject: project }),
      resetAnimation: () => set({
        animationStep: 0,
        currentAnimationProject: null,
      }),
    }),
    {
      name: 'rubby-storage',
      version: 4,
      partialize: (state) => ({
        // Always persist
        language: state.language,

        // Wizard state (THE CRITICAL FIX)
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        wizardStartTime: state.wizardStartTime,

        // Analytics
        abandonedSteps: state.abandonedSteps,

        // NOTE: storybookStep / animationStep are NOT persisted — always resume
        // from project status so profile deep-links are not clobbered by a
        // stale local step (or async rehydration racing a restore).
        // NOTE: uploadedFile is NOT persisted (File objects are not serializable)
        // NOTE: currentStorybookProject / currentAnimationProject are NOT persisted (may contain non-serializable data)
      }),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          const { toyName: _omitToyName, ...rest } = persistedState ?? {};
          return {
            ...rest,
            currentStep: rest.currentStep ?? 0,
            wizardStartTime: rest.wizardStartTime ?? null,
            abandonedSteps: rest.abandonedSteps ?? [],
          };
        }
        if (version === 1) {
          const { toyName: _omitToyName, ...rest } = persistedState ?? {};
          return { ...rest };
        }
        if (version === 2 || version === 3) {
          const {
            storybookStep: _omitSb,
            animationStep: _omitAn,
            ...rest
          } = persistedState ?? {};
          return { ...rest };
        }
        return persistedState as StoreState;
      },

    }
  )
);
